import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Copy, Radio, ScrollText } from "lucide-react"
import { Button } from "@ui/Button"
import { Card } from "@ui/Card"
import { EmptyState } from "@ui/EmptyState"
import { Switch } from "@ui/Switch"
import { toast } from "sonner"
import { MethodBadge } from "@components/workspace/MethodBadge"
import type { Project } from "@lib/inertia-types"

interface MockSummary {
  id: number
  name: string
  enabled: boolean
  priority: number
  environment_id: number | null
  response_status: number
  conditions_count: number
}

interface RequestNode {
  id: number
  name: string
  http_method: string
  url: string
  mocks_enabled: boolean
  mocks: MockSummary[]
}

interface ServiceNode {
  id: number
  name: string
  slug: string
  mocks_enabled: boolean
  requests: RequestNode[]
}

interface EnvNode {
  id: number
  name: string
  slug: string
  folder_id: number | null
}

export default function MocksIndex({
  project,
  environments,
  services,
  host,
}: {
  project: Project & { mock_token: string; mocks_enabled: boolean }
  environments: EnvNode[]
  services: ServiceNode[]
  host: string
}) {
  const anyMocks = services.some((s) => s.requests.some((r) => r.mocks.length > 0))

  function toggleProject(enabled: boolean) {
    router.patch(`/projects/${project.slug}`, { mocks_enabled: enabled }, { preserveScroll: true, only: ["project"] })
  }

  return (
    <>
      <Head title={`Mocks · ${project.name} · Fetch`} />
      <div className="mx-auto max-w-4xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-title3 font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
              Mocks
            </h1>
            <p className="mt-1 text-small text-cloverhound-gray-600">
              Public mock endpoints for IVR testing. Every environment serves
              at its own URL simultaneously.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Link href={`/projects/${project.slug}/mocks/logs`}>
              <Button variant="secondary">
                <ScrollText className="size-4" aria-hidden="true" />
                Live logs
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <span className="text-mini text-cloverhound-gray-600">Mocks enabled</span>
              <Switch checked={project.mocks_enabled} onCheckedChange={toggleProject} aria-label="Project mocks enabled" />
            </div>
          </div>
        </div>

        <MockBaseUrls project={project} environments={environments} services={services} host={host} />

        {!anyMocks ? (
          <EmptyState
            icon={<Radio className="size-6" aria-hidden="true" />}
            title="No mocks configured"
            description="Open a request in the workspace and add mock variants from its Mocks tab."
          />
        ) : (
          <div className="mt-6 flex flex-col gap-6">
            {services.map((service) => (
              <ServiceSection key={service.id} project={project} service={service} environments={environments} />
            ))}
          </div>
        )}
      </div>
    </>
  )
}

function MockBaseUrls({
  project,
  environments,
  services,
  host,
}: {
  project: Project & { mock_token: string }
  environments: EnvNode[]
  services: ServiceNode[]
  host: string
}) {
  const rows: { label: string; url: string }[] = []
  services.forEach((service) => {
    const serviceEnvs = environments.filter((e) => e.folder_id === service.id)
    if (serviceEnvs.length === 0) {
      rows.push({ label: service.name, url: `${host}/m/${project.mock_token}/${service.slug}/` })
    } else {
      serviceEnvs.forEach((env) => {
        rows.push({ label: `${service.name} · ${env.name}`, url: `${host}/m/${project.mock_token}/${env.slug}/${service.slug}/` })
      })
    }
  })

  if (rows.length === 0) return null

  return (
    <Card padding="md">
      <h2 className="mb-2 text-mini font-semibold uppercase tracking-wide text-cloverhound-gray-600">
        Mock base URLs
      </h2>
      <div className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <div key={row.url} className="flex items-center gap-2">
            <span className="w-40 shrink-0 truncate text-mini text-cloverhound-gray-700 dark:text-cloverhound-gray-300">
              {row.label}
            </span>
            <code className="min-w-0 flex-1 truncate rounded-sm bg-cloverhound-gray-100 px-2 py-1 font-mono text-micro text-cloverhound-gray-700 dark:bg-cloverhound-gray-850 dark:text-cloverhound-gray-300">
              {row.url}
            </code>
            <Button
              variant="ghost"
              size="iconXs"
              onClick={() => {
                navigator.clipboard.writeText(row.url)
                toast.success("Copied")
              }}
              aria-label="Copy URL"
            >
              <Copy className="size-3.5" aria-hidden="true" />
            </Button>
          </div>
        ))}
      </div>
    </Card>
  )
}

function ServiceSection({
  project,
  service,
  environments,
}: {
  project: Project
  service: ServiceNode
  environments: EnvNode[]
}) {
  function toggleService(enabled: boolean) {
    router.patch(`/projects/${project.slug}/folders/${service.id}`, { mocks_enabled: enabled }, {
      preserveScroll: true,
      only: ["services"],
    })
  }

  function toggleRequest(requestId: number, enabled: boolean) {
    router.patch(`/projects/${project.slug}/requests/${requestId}`, { mocks_enabled: enabled }, {
      preserveScroll: true,
      only: ["services"],
    })
  }

  const envName = (id: number | null) =>
    id === null ? "all envs" : environments.find((e) => e.id === id)?.name ?? "env"

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-small font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
          {service.name}
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-micro text-cloverhound-gray-500">service mocks</span>
          <Switch checked={service.mocks_enabled} onCheckedChange={toggleService} aria-label="Service mocks enabled" />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {service.requests
          .filter((r) => r.mocks.length > 0)
          .map((req) => (
            <Card key={req.id} padding="sm">
              <div className="flex items-center gap-2">
                <MethodBadge method={req.http_method} />
                <Link
                  href={`/projects/${project.slug}/requests/${req.id}`}
                  className="min-w-0 flex-1 truncate text-small font-medium text-cloverhound-gray-900 hover:text-cloverhound-indigo-600 dark:text-cloverhound-gray-50 dark:hover:text-cloverhound-indigo-300"
                >
                  {req.name}
                  <span className="ml-2 font-mono text-micro text-cloverhound-gray-500">{req.url}</span>
                </Link>
                <Switch checked={req.mocks_enabled} onCheckedChange={(v) => toggleRequest(req.id, v)} aria-label="Request mocks enabled" />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5 pl-8">
                {req.mocks.map((mock) => (
                  <span
                    key={mock.id}
                    className={`inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-micro ${
                      mock.enabled
                        ? "bg-cloverhound-green-50 text-cloverhound-green-700 dark:bg-cloverhound-green-500/10 dark:text-cloverhound-green-400"
                        : "bg-cloverhound-gray-100 text-cloverhound-gray-500 dark:bg-cloverhound-gray-850"
                    }`}
                  >
                    <Radio className="size-3" aria-hidden="true" />
                    {mock.name}
                    <span className="font-mono">{mock.response_status}</span>
                    <span className="text-cloverhound-gray-500">· {envName(mock.environment_id)}</span>
                    {mock.conditions_count > 0 && (
                      <span className="text-cloverhound-gray-500">· {mock.conditions_count} cond</span>
                    )}
                  </span>
                ))}
              </div>
            </Card>
          ))}
      </div>
    </div>
  )
}
